import React from 'react';

import { get } from 'lodash';
import PropTypes from 'prop-types';

import { catsearch } from '../../../textSearch';
import { isRootNode, ROOT_NODE_ID } from '../DynamicTree';
import CommonActionsTree from './CommonActionsTree';
import StatusAddon from '../NextTree/addons/StatusAddon';

class CommonAdapterTree extends CommonActionsTree {
  static propTypes = {
    ...CommonActionsTree.propTypes,
    isEditSessionActive: PropTypes.bool,
    adapter: PropTypes.shape({
      nodeIsReserved: PropTypes.func,
      nodeIsLeaf: PropTypes.func,
      resolveNodeTitle: PropTypes.func,
      resolveNodeTooltip: PropTypes.func,
      resolveNodeId: PropTypes.func,
      resolveNodeParentId: PropTypes.func,
      resolveNodeCustomText: PropTypes.func,
      resolveNodeClassName: PropTypes.func,
      renderNodeAddons: PropTypes.func,
      canRender: PropTypes.func,
    }),
  };

  static defaultProps = {
    ...CommonActionsTree.defaultProps,
    isEditSessionActive: undefined,
    adapter: {},
  };

  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      adapter: {
        nodeIsLeaf: this.nodeIsLeaf,
        nodeIsReserved: this.nodeIsReserved,
        nodeTitle: this.resolveNodeTitle,
        nodeTooltip: this.resolveNodeTooltip,
        nodeId: this.resolveNodeId,
        nodeParentId: this.resolveNodeParentId,
        renderNodeAddons: this.renderNodeAddons,
        canRender: props.adapter.canRender,
      },
      mainTreeChildrenCounts: {},
    };
  }

  nodeIsLeaf = node => {
    const {
      adapter: { nodeIsLeaf },
    } = this.props;

    if (nodeIsLeaf) {
      return nodeIsLeaf(node);
    }

    return false;
  };

  nodeIsReserved = node => {
    const {
      adapter: { nodeIsReserved },
    } = this.props;

    if (nodeIsReserved) {
      return nodeIsReserved(node);
    }

    return false;
  };

  resolveNodeTitle = node => {
    const {
      adapter: { resolveNodeTitle },
      syntheticRootNodeName,
    } = this.props;

    if (isRootNode(node)) {
      return syntheticRootNodeName;
    }

    if (resolveNodeTitle) {
      return resolveNodeTitle(node);
    }

    return node?.name;
  };

  resolveNodeTooltip = node => {
    const {
      adapter: { resolveNodeTooltip },
      syntheticRootNodeName,
    } = this.props;

    if (isRootNode(node)) {
      return syntheticRootNodeName;
    }

    if (resolveNodeTooltip) {
      return resolveNodeTooltip(node);
    }

    return node?.name;
  };

  makeNameQueryMatcher = searchQuery =>
    searchQuery
      ? node =>
          !isRootNode(node) &&
          catsearch(this.resolveNodeTitle(node), searchQuery)
      : null;

  resolveNodeId = node => {
    if (isRootNode(node)) {
      return ROOT_NODE_ID;
    }

    const {
      adapter: { resolveNodeId },
    } = this.props;

    if (resolveNodeId) {
      return resolveNodeId(node);
    }

    return node.id || node._tempId || node._id;
  };

  resolveNodeParentId = node => {
    const {
      adapter: { resolveNodeParentId },
      hasSyntheticRootNode,
    } = this.props;

    if (hasSyntheticRootNode && isRootNode(node)) {
      return null;
    }

    const resolvedParentId = resolveNodeParentId
      ? resolveNodeParentId(node)
      : node.parentId;

    if (
      hasSyntheticRootNode &&
      (!resolvedParentId || resolvedParentId === ROOT_NODE_ID)
    ) {
      return ROOT_NODE_ID;
    }

    return resolvedParentId;
  };

  resolveDefaultNodeClassName = node => {
    const {
      adapter: { nodeIsReserved, nodeIsLeaf, resolveNodeClassName },
    } = this.props;

    if (this.SyntheticRootNode === node) {
      return 'jstree-node-bold';
    }

    if (nodeIsLeaf && nodeIsLeaf(node)) {
      return 'jstree-node-italic';
    }

    if (nodeIsReserved && nodeIsReserved(node)) {
      return 'jstree-node-uppercase';
    }

    return '';
  };

  resolveNodeClassName = node => {
    const {
      adapter: { resolveNodeClassName },
    } = this.props;

    const defaultClassNames = this.resolveDefaultNodeClassName(node);
    if (resolveNodeClassName) {
      return resolveNodeClassName(node, defaultClassNames);
    }

    return defaultClassNames;
  };

  resolveNodeCustomText = node => {
    const {
      adapter: { resolveNodeCustomText },
    } = this.props;

    return resolveNodeCustomText && resolveNodeCustomText(node);
  };

  renderDefaultNodeAddons = node => (
    <StatusAddon key={`status_${node.id}`} status={node.status} />
  );

  renderNodeAddons = (item, options) => {
    const {
      adapter: { renderNodeAddons },
      isEditSessionActive,
    } = this.props;

    if (renderNodeAddons) {
      return renderNodeAddons(item, { isEditSessionActive, ...options });
    }

    return this.renderDefaultNodeAddons(item);
  };

  setMainTreeChildrenCounts = (nodeId, count) => {
    this.setState(state => ({
      mainTreeChildrenCounts: {
        ...state.mainTreeChildrenCounts,
        [nodeId]: count,
      },
    }));
  };

  getMainTreeChildrenCounts = nodeId =>
    get(this.state, ['mainTreeChildrenCounts', nodeId], 0);
}

export default CommonAdapterTree;
